import axios from "axios";
import store from "../store";
import React, { useEffect, useRef, useState } from 'react';
import { logout } from "../redux/slices/authSlice";
import SessionExpiredModal from "../components/SessionExpiredModal";
import {
  clearSessionExpiryTimer,
  isStoredJwtExpired,
  notifySessionExpired,
  resetSessionExpiredNotice,
  scheduleSessionExpiryWatch,
  shouldIgnoreUnauthorizedUrl,
  subscribeSessionExpired,
} from "./sessionExpiry";

const AxiosInterceptor = ({ children }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const showModalRef = useRef(() => {});

  const showModal = () => {
    setIsModalVisible(true);
  };
  showModalRef.current = showModal;

  const handleOk = () => {
    setIsModalVisible(false);
    clearSessionExpiryTimer();
    resetSessionExpiredNotice();
    store.dispatch(logout());
    window.location.href = '/login';
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest) {
          return Promise.reject(error);
        }
        if (originalRequest._retry === undefined) {
          originalRequest._retry = false;
        }
        const errorName = error?.response?.data?.error?.name;
        const status = error?.response?.status;
        const requestUrl = String(originalRequest?.url || originalRequest?.baseURL || '');

        if (
          requestUrl.toLowerCase().includes('/api/user/guide-videos-link') ||
          requestUrl.toLowerCase().includes('guide-videos-link') ||
          requestUrl.toLowerCase().includes('/api/user/manage-profile-link') ||
          requestUrl.toLowerCase().includes('manage-profile-link')
        ) {
          return Promise.reject(error);
        }

        const isExpiredUnauthorized =
          (errorName === "TokenExpiredError" || status === 401) &&
          !shouldIgnoreUnauthorizedUrl(requestUrl);

        if (isExpiredUnauthorized && !originalRequest._retry) {
          originalRequest._retry = true;
          notifySessionExpired();
        }

        return Promise.reject(error);
      }
    );

    const unsubscribe = subscribeSessionExpired(() => {
      showModalRef.current();
    });

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (isStoredJwtExpired()) {
        notifySessionExpired();
        return;
      }
      scheduleSessionExpiryWatch();
    };

    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);
    scheduleSessionExpiryWatch();

    return () => {
      axios.interceptors.response.eject(interceptorId);
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
      clearSessionExpiryTimer();
    };
  }, []);

  return (
    <>
      {children}
      <SessionExpiredModal open={isModalVisible} onOk={handleOk} />
    </>
  );
};

export default AxiosInterceptor;
